import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Switch,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { usePublisher } from '../hooks/usePublisher'

export function PublisherPage() {
  const {
    closeCreatePublisher,
    closeEditPublisher,
    createDialogOpen,
    createForm,
    createFormErrors,
    createPublisher,
    deletePublisher,
    disciplineOptions,
    editForm,
    editingPublisher,
    filteredPublishers,
    filters,
    loading,
    openCreatePublisher,
    openEditPublisher,
    saving,
    isCreateFormValid,
    updateCreateFormField,
    setCreateForm,
    setCreateFormErrors,
    setEditForm,
    setFilters,
    setSnackbar,
    snackbar,
    updatePublisher,
  } = usePublisher()

  const getDisciplineLabel = (discipline: string | null) =>
    disciplineOptions.find((option) => option.internal_value === discipline)?.display_value || discipline || '—'

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Users</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Manage portal users, their roles, and publisher discipline assignments.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            label="Search users"
            value={filters.search || ''}
            onChange={(event) =>
              setFilters((current) => ({ ...current, search: event.target.value }))
            }
            sx={{ minWidth: 260 }}
          />
          <TextField
            select={true}
            size="small"
            label="Role"
            value={filters.role || 'all'}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                role: event.target.value as typeof current.role,
              }))
            }
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All roles</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="author">Author</MenuItem>
            <MenuItem value="publisher">Publisher</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={openCreatePublisher}
          >
            Create User
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Discipline</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredPublishers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filteredPublishers.map((publisher) => (
                <TableRow key={publisher.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                    #{publisher.id}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{publisher.name}</TableCell>
                  <TableCell>{publisher.email}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{publisher.role}</TableCell>
                  <TableCell>{publisher.is_active ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>{getDisciplineLabel(publisher.discipline)}</TableCell>
                  <TableCell>{new Date(publisher.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEditPublisher(publisher)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ color: 'error.main' }}
                        onClick={() => void deletePublisher(publisher)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(editingPublisher)} onClose={closeEditPublisher} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              size="small"
              label="Full Name"
              value={editForm.name}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, name: event.target.value }))
              }
            />
            <TextField
              size="small"
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, email: event.target.value }))
              }
            />
            <TextField
              select={true}
              size="small"
              label="Role"
              value={editForm.role}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  role: event.target.value as typeof current.role,
                  discipline: event.target.value === 'publisher' ? current.discipline : '',
                }))
              }
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="author">Author</MenuItem>
              <MenuItem value="publisher">Publisher</MenuItem>
            </TextField>
            <TextField
              select={true}
              size="small"
              label="Discipline"
              value={editForm.discipline}
              onChange={(event) =>
                setEditForm((current) => ({ ...current, discipline: event.target.value }))
              }
              helperText="Optional"
            >
              <MenuItem value="">No discipline</MenuItem>
              {disciplineOptions.map((option) => (
                <MenuItem key={option.id} value={option.internal_value}>
                  {option.display_value}
                </MenuItem>
              ))}
            </TextField>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Active user</Typography>
              <Switch
                checked={editForm.is_active}
                onChange={(event) =>
                  setEditForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
            </Box>
            <TextField
              size="small"
              label="New Password"
              type="password"
              value={editForm.password || ''}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  password: event.target.value,
                }))
              }
              helperText="Leave blank to keep the current password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditPublisher}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void updatePublisher()}
            disabled={
              saving ||
              !editForm.name.trim() ||
              !editForm.email.trim()
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={closeCreatePublisher} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Box
            id="create-publisher-form"
            component="form"
            onSubmit={(event) => {
              event.preventDefault()

              if (
                saving ||
                !createForm.name.trim() ||
                !createForm.email.trim() ||
                !createForm.password.trim() ||
                !isCreateFormValid
              ) {
                return
              }

              void createPublisher()
            }}
            display="flex"
            flexDirection="column"
            gap={2}
            mt={1}
          >
            <TextField
              size="small"
              label="Full Name"
              value={createForm.name}
              error={Boolean(createFormErrors.name)}
              helperText={createFormErrors.name}
              onChange={(event) => {
                updateCreateFormField('name', event.target.value)
              }}
              required={true}
            />
            <TextField
              size="small"
              label="Email"
              type="email"
              value={createForm.email}
              error={Boolean(createFormErrors.email)}
              helperText={createFormErrors.email}
              onChange={(event) => {
                updateCreateFormField('email', event.target.value)
              }}
              required={true}
            />
            <TextField
              size="small"
              label="Password"
              type="password"
              value={createForm.password}
              error={Boolean(createFormErrors.password)}
              helperText={
                createFormErrors.password ||
                'Min 12 chars, uppercase, lowercase, number, and special character.'
              }
              onChange={(event) => {
                updateCreateFormField('password', event.target.value)
              }}
              required={true}
            />
            <TextField
              select={true}
              size="small"
              label="Role"
              value={createForm.role}
              onChange={(event) => {
                updateCreateFormField('role', event.target.value)
                if (event.target.value !== 'publisher') {
                  updateCreateFormField('discipline', '')
                }
              }}
              required={true}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="author">Author</MenuItem>
              <MenuItem value="publisher">Publisher</MenuItem>
            </TextField>
            <TextField
              select={true}
              size="small"
              label="Discipline"
              value={createForm.discipline}
              error={Boolean(createFormErrors.discipline)}
              helperText={
                createFormErrors.discipline ||
                'Optional'
              }
              onChange={(event) => {
                updateCreateFormField('discipline', event.target.value)
              }}
              required={false}
            >
              <MenuItem value="">No discipline</MenuItem>
              {disciplineOptions.map((option) => (
                <MenuItem key={option.id} value={option.internal_value}>
                  {option.display_value}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreatePublisher}>Cancel</Button>
          <Button
            form="create-publisher-form"
            type="submit"
            variant="contained"
            disabled={
              saving ||
              !createForm.name.trim() ||
              !createForm.email.trim() ||
              !createForm.password.trim() ||
              !isCreateFormValid
            }
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((current) => ({ ...current, open: false }))
        }
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}
